import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AllQuestionsWrapper,
  Flex,
  InnerContainer,
  InnerFlex,
  InnerWrapper,
  LeftArrowBtn,
  RightArrowBtn,
  RowsBox,
  ScalesBox,
  SliderContainer,
  SliderWrapper,
  SwitcherBlock,
  SwitchersWrapper,
  Text,
  TitleText,
  Wrapper,
} from './FiveChallengesCarousel.styles';
import TopicLevelChart from './TopicLevelChart/TopicLevelChart';
import parseRelatedTopics from '../../../../../../helpers/effectReports/parseRelatedTopics';
import { defaultScales } from '../../../../../../helpers/helpers';
import { useReportContext } from '../../../../../../hooks/useReport';
import { Analysis, IToggleState } from '../../../../../../services/reports';
import ArrowButton from '../../../../../ArrowButton/ArrowButton';

interface FiveChallengesCarouselProps {
  data: Analysis;
  activeTopic: number;
  setActiveTopic: (arg: number) => void;
  insightsEntity: {
    clarity?: Analysis;
    quality?: Analysis;
    relationship?: Analysis;
    behaving?: Analysis;
    learning?: Analysis;
  };
  togglersState: IToggleState;
  challengeName?: string;
}

const order = [
  'EFFECT CLARITY',
  'EFFECT QUALITY',
  'EFFECT RELATIONSHIP',
  'EFFECT BEHAVING',
  'EFFECT LEARNING',
];

const FiveChallengesCarousel: React.FC<FiveChallengesCarouselProps> = ({
  activeTopic,
  setActiveTopic,
  insightsEntity,
  togglersState,
  challengeName,
}) => {
  const { effectData } = useReportContext();
  const translateX = `-${activeTopic * 100}%`;
  const { t } = useTranslation();

  const reorderedArray = Object.values(insightsEntity).sort((a, b) => {
    return order.indexOf(a.type) - order.indexOf(b.type);
  });

  const nextTopic = () => {
    if (activeTopic < Object.values(insightsEntity).length - 1) {
      setActiveTopic(activeTopic + 1);
    } else {
      setActiveTopic(0);
    }
  };

  const previousTopic = () => {
    if (activeTopic > 0) {
      setActiveTopic(activeTopic - 1);
    } else {
      setActiveTopic(Object.values(insightsEntity).length - 1);
    }
  };

  const handleTopicSwitch = (id: number) => () => {
    setActiveTopic(id);
  };

  const renderSlides = () =>
    reorderedArray.map((data, index) => {
      const parsedData = parseRelatedTopics(data, effectData);
      return (
        <InnerContainer key={index}>
          <Flex>
            <TitleText>{challengeName || t('report.effect.challenge')}</TitleText>
            <InnerFlex>
              <Text>{data.description}</Text>
              {/* Scales removed for now */}
              {/* <div>
                <TextLight>{t('report.effect.insights.scales')}:</TextLight>
                <div>
                  {parsedData.map(({ label }) => (
                    <TextLight key={label}>{label}</TextLight>
                  ))}
                </div>
              </div> */}
            </InnerFlex>
          </Flex>
          <Flex>
            <TitleText>{t('report.effect.insights.topicLevel')}</TitleText>
            <InnerFlex>
              <AllQuestionsWrapper>
                <ScalesBox>
                  {defaultScales.map(item => (
                    <div key={item}>{item}</div>
                  ))}
                </ScalesBox>
                <RowsBox>
                  {parsedData.map(item => (
                    <TopicLevelChart key={item.label} data={item} togglersState={togglersState} />
                  ))}
                </RowsBox>
              </AllQuestionsWrapper>
            </InnerFlex>
          </Flex>
        </InnerContainer>
      );
    });

  return (
    <Wrapper>
      <InnerWrapper>
        {Object.values(insightsEntity).length > 1 && (
          <LeftArrowBtn>
            <ArrowButton onClick={previousTopic} left />
          </LeftArrowBtn>
        )}
        <SliderWrapper>
          <SliderContainer translateX={translateX}>{renderSlides()}</SliderContainer>
        </SliderWrapper>

        {Object.values(insightsEntity).length > 1 && (
          <RightArrowBtn>
            <ArrowButton onClick={nextTopic} />
          </RightArrowBtn>
        )}
      </InnerWrapper>
      <SwitchersWrapper>
        {Object.values(insightsEntity).map((item, index) => {
          const isActive = index === activeTopic;
          return (
            <SwitcherBlock
              key={item.description}
              isActive={isActive}
              onClick={handleTopicSwitch(index)}
            />
          );
        })}
      </SwitchersWrapper>
    </Wrapper>
  );
};

export default FiveChallengesCarousel;
